import { graphql, useStaticQuery } from "gatsby";
import React, {
ChangeEvent,
FormEvent,
FunctionComponent,
useState,
} from "react";
import styled from "styled-components";
import useAuth from "../../hooks/useAuth";
import { useSiteDefaultsQuery } from "../../hooks/useSiteDefaultsQuery";
import { getErrorMessage } from "../../utils";
import { RegistrationData, RegistrationFormProps } from "../interfaces";
import { SelectTextField } from "./fields";
import Input from "./input";
import { RadioGroup } from "./radio";
import Submit from "./submit";

const initialFormData = {
first_name: "",
last_name: "",
email: "",
autoLogin: true,
redirect: "/virtual/live",
};

const RegistrationForm: FunctionComponent<RegistrationFormProps> = ({
   onFocus,
  onSubmit,
  onError,
}) => {
   const { signUp } = useAuth();
  const [error, setError] = useState<string>();
  const [formData, updateFormData] =
    useState<RegistrationData>(initialFormData);
   const [registrationTypeError, setRegistrationTypeError] = useState(false);
  const { countries } = useSiteDefaultsQuery();
  // TODO: type check this
  const { allStrapiTicketType } = useStaticQuery(graphql`
     query ticketTypes {
       allStrapiTicketType {
         nodes {
           name
           cost: price
           label
           value
         }
       }
     }
   `);
   const tickets = allStrapiTicketType.nodes;
   const tickets2 = [
    {
      name: "registration_type",
      cost: 0,
      label: "Government",
      value: "gditot-24-gov"
    },
    {
      name: "registration_type",
      cost: 0,
      label: "Press",
      value: "gditot-24-press"
    },
    {
      name: "registration_type",
      cost: 0,
      label: "Industry",
      value: "gditot-24-industry"
    }
  ];
   const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
     const field = event.currentTarget;
     const getValue = () => {
       switch (field.type) {
         case "checkbox":
           return field.checked;
        case "select":
          return field.value;
        default:
          return field?.value?.trim();
      }
     };
     updateFormData({
       ...formData,
       [field.name]: getValue(),
     });
  };

   const handleOnRadioGroupChange = (option: Record<string, string>) => {
     updateFormData({ ...formData, ...option });
  };

   const validateRadioGroups = () => {
     if (!Object.keys(formData).includes("registration_type")) {
       setRegistrationTypeError(true);
       onError &&
         onError(
           "Please select either Goverment, Industry or Press ticket type."
         );
       throw new Error("failed validation");
     } else {
       setRegistrationTypeError(false);
     }
   };

   const handleOnSubmit = async (event: FormEvent) => {
     event.preventDefault();
     onSubmit && onSubmit();
     try {
       validateRadioGroups();
       await signUp(formData);
     } catch (error) {
       setError(getErrorMessage(error));
       onError && onError(error);
     }
   };

   return (
     <StyledRegistrationForm className="form-register">
       {error && <div>{error}</div>}
       <form onFocus={onFocus} onSubmit={handleOnSubmit}>
         <div className="lg">
           <RadioGroup
             required
             options={tickets2}
             onChange={handleOnRadioGroupChange}
           />
         </div>
         <Input
           required
           className="field"
           placeholder="First Name"
           type="text"
           name="first_name"
           id="registration_first_name"
           onChange={handleOnChange}
         />
         <Input
           required
           className="field"
           placeholder="Last Name"
           type="text"
           name="last_name"
           id="registration_last_name"
           onChange={handleOnChange}
         />
         <Input
           required
           className="field"
           placeholder="Email"
           type="email"
           name="email"
           id="registration_email"
           onChange={handleOnChange}
         />
         <Input
           required
           className="field"
           placeholder="Job Title"
           type="text"
           name="job_title"
           id="registration_job_title"
           onChange={handleOnChange}
         />
         <Input
           required
           className="field"
           placeholder="Company"
           type="text"
           name="company"
           id="registration_company"
           onChange={handleOnChange}
         />
         <Input
           required
           className="field"
           placeholder="Work Phone"
           type="tel"
           name="work_phone"
           id="registration_work_phone"
           onChange={handleOnChange}
         />
         <SelectTextField
           required
           className="field"
           options={countries}
           placeholder="Country*"
           type="select"
           name="Country"
           id="registration_country"
           // TODO: TYPECHECK currentTarget
           onChange={(option) =>
             handleOnChange({
               currentTarget: {
                 name: "country",
                 value: option?.name,
                 type: "select",
               },
             })
           }
         />
         <Input
           required
           className="field"
           placeholder="Zip Code"
           type="text"
           name="zip-code"
           id="registration_zip_code"
           onChange={handleOnChange}
         />

         <p className="lg info italic smaller" style={{ marginBlock: "15px" }}>
           <em style={{ fontSize: "10px" }}>
             By submitting my contact information, I consent to FedScoop LLC
             processing of my data, contacting me and sharing my information with
             its partners. I acknowledge that information submitted may be used
             by and associated with other information FedScoop LLC has collected
             and used pursuant to its privacy notice (
             <a href="https://scoopnewsgroup.com/privacy/" target="__blank">
               https://scoopnewsgroup.com/privacy/
             </a>
             ). For a full list of sponsors, go to:
             <a href="https://gditot.upgather.com/#sponsors" target="__blank">
               https://gditot.upgather.com/#sponsors
             </a>
           </em>
         </p>
         <Submit className="lg footer submit" type="submit" value="Register" />
       </form>
       {error && <div>{error}</div>}
     </StyledRegistrationForm>
   );
 };

 export const StyledRegistrationForm = styled.div`
   form {
     display: grid;
     grid-template-columns: 1fr 1fr;
     grid-gap: 15px;
     .field .react-select__control {
       border: 0;
       color: #646464;
       background-color: #f0f0f0;
     }
   }
   .lg {
     grid-column: 1/3;
   }
   .industry.error label {
     color: red;
   }
   .industry label {
     color: #646464;
   }
   .info {
     color: grey;
     font-size: 12px;
     margin-bottom: -10px;
   }
   p {
     margin: 0px;
   }
   .footer {
     margin-top: 0px;
   }
   @media only screen and (min-width: 320px) and (max-width: 767px) {
     form {
       display: block;
       input[type="text"] {
         margin: 15px 0px;
       }
       input[type="checkbox"] {
         margin: 5px 0px;
       }
     }
     p.info {
       margin-bottom: 10px;
     }
   }
 `;

export default RegistrationForm;

// Need to validate tickets and type settings for this component
// export default null;